import React, { useContext } from 'react';
import get from 'lodash/get';
import min from 'lodash/min';
import max from 'lodash/max';
import styled from 'styled-components';
import parse from 'html-react-parser';
import { THit } from '@com/core/common/types/catalog';
import { getProductUrl } from '@com/core/common/utils/product';
import LazyLoad from 'react-lazyload';
import MediaPreloader from '@com/core/common/components/mediaPreloader';
import { getTransformationUrlV2 } from '@com/core/common/utils/image';
import { AppContext } from '@com/core/common/contexts/appContext';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
// import { NavContext } from '@com/core/common/contexts/navContext';
import { useTranslation /* Router */ } from '@com/core/common/translations/i18n';
import Link from '@com/core/common/components/Link';
import Truncate from 'react-truncate';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import ProductPrice from '../common/ProductPrice';
import QuickAddToCart from '../common/QuickAddToCart';

type TProps = {
    eventLocation: 'page_catalog' | 'page_static' | 'recos_page-detail' | 'recos_page-static';
    product: THit;
    isRecommended?: boolean;
    titleWidth: number;
    showSubscriptionModal: (show: boolean, skuConfig: string) => void;
    isSubscriptionModal: boolean;
};

const ProductBoxPLPDesktop = ({
    eventLocation,
    isRecommended = false,
    isSubscriptionModal,
    product,
    showSubscriptionModal,
    titleWidth,
}: TProps): JSX.Element => {
    const { t } = useTranslation('common');

    // const [isNavigating, setIsNavigating] = useState(false);
    const { locale } = useContext(AppContext);
    const { customer, showLoginModal } = useContext(CustomerContext);
    // const { navTo } = useContext(NavContext);

    const imageLoader = (): JSX.Element => {
        return (
            <MediaPreloader productImage>
                <img src={`${CDN_IMG_BASE}/images/loader-product-image.png`} alt="loading" />
            </MediaPreloader>
        );
    };

    const itemCount = get(product.plp_specifications, 'Item Count');

    const renderSubscribeAndSave = () => {
        const stages =
            product.subscription && product.subscription.is_subscribable && product.subscription.sub_discount
                ? product.subscription.sub_discount.stages
                : null;
        if (!stages) return null;
        const minValue = min(stages);
        const maxValue = stages.length > 1 ? max(stages) : null;
        const discountString = maxValue ? `${minValue}-${maxValue}%` : `${minValue}%`;
        return (
            <SubscriptionContent>
                <p>{discountString}</p>
                <button
                    onClick={() => {
                        if (customer) {
                            showSubscriptionModal(isSubscriptionModal, product.sku_config);
                        } else {
                            showLoginModal(true);
                        }
                    }}
                    type="button">
                    {t('subscribe_now')}
                </button>
            </SubscriptionContent>
        );
    };
    // const stages =
    // const minValue = min(stages);
    // const maxValue = stages.length > 1 ? max(stages) : null;
    // const discountString = max ? `${min}-${max}%` : `${min}%`;

    // cli todo - i'd like to reintroduce this
    // const navigateToPdp = (e: SyntheticEvent<HTMLElement>): void => {
    //     e.preventDefault();
    //     setIsNavigating(true);
    //     navTo(`/${getProductUrl(product, true)}`);
    //     Router.events.on('routeChangeComplete', () => {
    //         setIsNavigating(false);
    //     });
    //     Router.events.on('routeChangeError', () => setIsNavigating(false));
    // };

    const renderInstantTag = () => {
        if (product.flags.includes('instant')) {
            if (isRecommended) {
                return <InstantTag src={`${CDN_IMG_BASE}/creativeIcons/instant-bicycle.svg`} alt="instant" />;
            }
            return (
                <InstantTag
                    src={`${CDN_IMG_BASE}/creativeIcons/instant-${locale.isArabic ? 'ar' : 'en'}.svg`}
                    alt="instant"
                />
            );
        }
        return null;
    };

    return (
        <Container>
            {/* {isNavigating && (
                    <LoaderCtr>
                        <Loader type="bounce" dotSize={10} color={colors.brandSecondary} />
                    </LoaderCtr>
                )} */}
            <ImageContainer>
                <Link href={getProductUrl(product, true)}>
                    <div>
                        <LazyLoad placeholder={imageLoader()} offset={300}>
                            {isRecommended ? (
                                <img
                                    src={getTransformationUrlV2(product.image_key, '240')}
                                    alt={product.name}
                                    className="productImage"
                                />
                            ) : (
                                <MediaPreloader productImage>
                                    <img
                                        src={getTransformationUrlV2(product.image_key, '240')}
                                        alt={product.name}
                                        className="productImage"
                                    />
                                </MediaPreloader>
                            )}
                        </LazyLoad>
                        {product.catalog_tag && <TagContainer>{parse(product.catalog_tag)}</TagContainer>}
                        {renderInstantTag()}
                    </div>
                </Link>

                <QuickAddToCart
                    isRecommended={isRecommended}
                    eventLocation={eventLocation}
                    offerCode={product.offer_code}
                    sku={product.sku_config}
                    stock={product.stock}
                />
            </ImageContainer>
            <DetailsContainer>
                <Link href={getProductUrl(product, true)} linkClass="detailsContainerLink">
                    <>
                        <ProductPrice
                            isRecommended={isRecommended}
                            price={product.price}
                            salePrice={product.sale_price}
                            size="regular"
                        />
                        <>
                            <p id="title" className="title">
                                <Truncate width={titleWidth} lines={2}>
                                    {product.brand} {product.name}
                                </Truncate>
                            </p>
                            {itemCount && <p className="itemCount">{itemCount}</p>}
                        </>
                        <p className="deliveryEstimate">
                            <img src={`${CDN_IMG_BASE}/simpleIcons/delivery-rocket-grey.svg`} alt="Arrives" />
                            {t('arrives')}&nbsp;<strong>{product.estimated_delivery}</strong>
                        </p>
                    </>
                </Link>
                {!isRecommended && renderSubscribeAndSave()}
            </DetailsContainer>
        </Container>
    );
};

const SubscriptionContent = styled.div`
    display: flex;
    height: 25px;
    > p {
        flex: 1;
        color: ${({ theme }): string => theme.colors.bodyAlt};
        background-color: #f68721;
        border-radius: 2px 0px 0px 8px;
        font-size: 0.625rem;
        align-items: center;
        display: flex;
        justify-content: center;
        font-weight: 600;
        ${({ theme: { right } }): string => `margin-${right}: 4px`};
    }
    > button {
        flex: 2;
        background-color: rgba(246, 135, 33, 0.12);
        border-radius: 2px;
        font-size: 0.625rem;
        font-weight: 600;
    }
`;

const Container = styled.div`
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    height: 100%; /* might be for carousel uses */
    cursor: pointer;
    display: flex;
    flex-direction: column;
    position: relative;
`;

const ImageContainer = styled.div`
    position: relative;
    min-height: 190px;
`;

const TagContainer = styled.p`
    position: absolute;
    top: 0;
    ${({ theme: { left } }): string => `${left}: 0`};
    color: ${({ theme }): string => theme.colors.bodyAlt};
    background-color: ${({ theme }): string => theme.colors.brandPrimary};
    font-size: 0.625rem;
    line-height: 1.3;
    padding: 3px 6px;
`;

const DetailsContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 12px 0 16px;

    .detailsContainerLink {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .title {
        font-size: 13px;
        line-height: 1.4;
    }

    .itemCount {
        margin-top: 2px;
        font-size: 12px;
        color: ${({ theme }): string => theme.colors.bodyLight};
    }

    .deliveryEstimate {
        padding-top: 12px;
        display: flex;
        align-items: center;
        font-size: 0.8125rem;
        color: ${({ theme }): string => theme.colors.bodyLight};

        img {
            ${({ theme: { right } }): string => `margin-${right}: 4px`};
            width: 20px;
        }

        strong {
            color: ${({ theme }): string => theme.colors.brandPrimary};
            font-weight: 600;
        }
    }

    .subscription {
        padding-top: 4px;
        display: flex;
        align-items: center;
        font-size: 0.8125rem;
        color: ${({ theme }): string => theme.colors.bodyLight};
        text-transform: capitalize;

        img {
            padding: 2px;
            ${({ theme: { right } }): string => `margin-${right}: 4px`};
            width: 20px;
        }

        strong {
            color: ${({ theme }): string => theme.colors.brandPrimary};
            font-weight: 600;
        }
    }
`;

const InstantTag = styled.img`
    position: absolute;
    bottom: 0;
    height: 20px;
`;

export default ProductBoxPLPDesktop;
